import { EnclosureType } from '../../types/enclosure/enclosure.type';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { additionalQueryParams } from '../../utils/utilities';
import { setModalContent, setShowModal } from '../utils/utils.slice';
import { AsyncTaskType } from '../../types/async-task/async-task.type';
import { rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';

export const postEnclosures = createAsyncThunk<
  AsyncTaskType,
  {
    data: {
      warehouse: { id: number };
      equipment_group: { id: number };
      enclosures: Array<unknown>;
    };
  },
  { state: RootReducerState }
>('enclosures/postEnclosures', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<AsyncTaskType>(`${process.env.REACT_APP_API_URL}/enclosures/bulk`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating enclosures!' });
  }
});

export const postEnclosureDetails = createAsyncThunk<EnclosureType, { data: unknown }, { state: RootReducerState }>(
  'enclosures/postEnclosureDetails',
  async ({ data }, thunkAPI) => {
    try {
      const response = await axios.post<EnclosureType>(`${process.env.REACT_APP_API_URL}/enclosures`, data);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating enclosure!' });
    }
  }
);

export const getEnclosuresList = createAsyncThunk<
  PaginationType<EnclosureType>,
  RouteParamsType & {
    controller?: AbortController;
  },
  { state: RootReducerState }
>('enclosures/getEnclosuresList/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().sharedReducer.selectedCountry
    );
    const mergedParams = { ...params, ...additionalParams };
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<EnclosureType>>(
      `${process.env.REACT_APP_API_URL}/enclosures${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching enclosures!' });
  }
});

export const getEnclosureDetails = createAsyncThunk<EnclosureType, { id: number }, { state: RootReducerState }>(
  'enclosures/getEnclosureDetails',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<EnclosureType>(`${process.env.REACT_APP_API_URL}/enclosures/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching enclosure!' });
    }
  }
);

export const patchEnclosureDetails = createAsyncThunk<
  EnclosureType,
  { id: number; data: unknown },
  {
    state: RootReducerState;
  }
>('enclosures/patchEnclosureDetails', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<EnclosureType>(`${process.env.REACT_APP_API_URL}/enclosures/${id}`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating enclosure!' });
  }
});

export const deleteEnclosureDetails = createAsyncThunk<
  EnclosureType,
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('enclosures/deleteEnclosureDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.delete<EnclosureType>(`${process.env.REACT_APP_API_URL}/enclosures/${id}`);
    if (fetchList) thunkAPI.dispatch(getEnclosuresList(thunkAPI.getState().enclosuresReducer.enclosuresRouteParams));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting enclosure!' });
  }
});

export const postEnclosureOwnershipTransfer = createAsyncThunk<
  void,
  {
    id: number;
    data: {
      owner: { id: number };
    };
  },
  { state: RootReducerState; rejectValue: string }
>('enclosures/postEnclosureOwnershipTransfer', async ({ id, data }, thunkAPI) => {
  try {
    if (!data.owner?.id) {
      return rejectWithApiError({ thunkAPI, error: null, fallback: 'Owner organization is required!' });
    }

    await axios.post(`${process.env.REACT_APP_API_URL}/enclosures/${id}/ownership-transfer`, data);
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during Enclosure ownership transfer!' });
  }
});
